import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import PropertyCard from "@/components/PropertyCard";
import AlertDrawer from "@/components/AlertDrawer";
import FilterDrawer from "@/components/FilterDrawer";
import { Header, FloatingChat, Footer } from "@/components/layout";
import { SearchResultsHeader, FilterButton, AlertButton, type SortOption } from "@/components/search";
import SearchBar from "@/components/search/SearchBar";
import { PropertyMap, type PropertyLocation } from "@/components/map";
import { Button } from "@/components/ui/Button";
import { EyeClosed, Heart } from "lucide-react";
import { useImobiliaria } from "@/hooks/useImobiliaria";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useImoveis, type ImoveisFilters } from "@/hooks/useImoveis";
import { useFavoritesContext } from "@/components/providers/FavoritesProvider";
import { imoveisToPropertyCards } from "@/adapters/propertyAdapter";
import { MOCK_FEATURED_PROPERTIES } from "@/data/mockData";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGeocodedProperties } from "@/hooks/useGeocodedProperties";

export default function SearchGridPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Função para atualizar URL com todos os filtros
  const updateUrlWithFilters = useCallback((newFilters: ImoveisFilters, sort: SortOption, page: number) => {
    const params = new URLSearchParams();
    
    // Filtros básicos
    if (newFilters.finalidade) params.set('finalidade', newFilters.finalidade);
    if (newFilters.tipo?.length) params.set('tipo', newFilters.tipo.join(','));
    if (newFilters.categoria) params.set('categoria', newFilters.categoria);
    if (newFilters.padrao) params.set('padrao', newFilters.padrao);
    if (newFilters.search) params.set('search', newFilters.search);
    
    // Valores numéricos
    if (newFilters.quartos_min) params.set('quartos_min', String(newFilters.quartos_min));
    if (newFilters.suites_min) params.set('suites_min', String(newFilters.suites_min));
    if (newFilters.banheiros_min) params.set('banheiros_min', String(newFilters.banheiros_min));
    if (newFilters.vagas_min) params.set('vagas_min', String(newFilters.vagas_min));
    if (newFilters.area_min) params.set('area_min', String(newFilters.area_min));
    if (newFilters.area_max) params.set('area_max', String(newFilters.area_max));
    if (newFilters.valor_min) params.set('valor_min', String(newFilters.valor_min));
    if (newFilters.valor_max) params.set('valor_max', String(newFilters.valor_max));
    
    // Diferenciais (arrays)
    if (newFilters.diferenciais?.length) params.set('diferenciais', newFilters.diferenciais.join(','));
    if (newFilters.diferenciais_condominio?.length) params.set('diferenciais_cond', newFilters.diferenciais_condominio.join(','));
    
    // Ordenação e página
    if (sort !== 'recent') params.set('sort', sort);
    if (page > 1) params.set('page', String(page));
    
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);
  
  const [activeTab, setActiveTab] = useState("Todos");
  const [isAlertDrawerOpen, setIsAlertDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMap, setShowMap] = useState(false);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | number | null>(null);
  const [filters, setFilters] = useState<ImoveisFilters>({});
  const [sortOrder, setSortOrder] = useState<SortOption>('recent');
  const pageSize = 15; // 15 cards por página (3 linhas de 5 colunas sem mapa)

  // Função para obter o valor de referência do imóvel para ordenação por preço
  const getPropertyReferenceValue = (imovel: any, finalidade?: 'locacao' | 'venda') => {
    if (finalidade === 'locacao') return imovel.valor_locacao ?? Infinity;
    if (finalidade === 'venda') return imovel.valor_venda ?? Infinity;
    // "Todos" - usar o menor valor disponível
    const venda = imovel.valor_venda ?? Infinity;
    const locacao = imovel.valor_locacao ?? Infinity;
    return Math.min(venda, locacao);
  };

  // Mapeamento de ordenação para o hook useImoveis (exceto preço que é ordenado localmente)
  const getOrderBy = (sort: SortOption) => {
    const mapping: Record<SortOption, { column: string; ascending: boolean }> = {
      recent: { column: 'created_at', ascending: false },
      oldest: { column: 'created_at', ascending: true },
      price_asc: { column: 'created_at', ascending: false }, // Preço será ordenado localmente
      price_desc: { column: 'created_at', ascending: false }, // Preço será ordenado localmente
      area_desc: { column: 'area', ascending: false },
      area_asc: { column: 'area', ascending: true },
    };
    
    return mapping[sort];
  };

  // Abrir mapa por padrão no desktop
  useEffect(() => {
    if (!isMobile) {
      setShowMap(true);
    }
  }, [isMobile]);

  // Buscar dados da imobiliária
  const { data: imobiliaria } = useImobiliaria('desordibroker');
  const { data: siteConfig } = useSiteConfig(imobiliaria?.id);
  
  // Buscar favoritos
  const { favorites, count: favoritesCount } = useFavoritesContext();
  
  // Buscar imóveis com filtros e ordenação (backend)
  const { data: imoveis, isLoading: loadingImoveis } = useImoveis({
    imobiliariaId: imobiliaria?.id,
    filters,
    orderBy: getOrderBy(sortOrder),
  });

  // Ordenar imóveis localmente para preço (suporta "Todos" com menor valor disponível)
  const sortedImoveis = useMemo(() => {
    if (!imoveis) return [];
    
    if (sortOrder === 'price_asc' || sortOrder === 'price_desc') {
      return [...imoveis].sort((a, b) => {
        const valA = getPropertyReferenceValue(a, filters.finalidade);
        const valB = getPropertyReferenceValue(b, filters.finalidade);
        return sortOrder === 'price_asc' ? valA - valB : valB - valA;
      });
    }
    
    return imoveis;
  }, [imoveis, sortOrder, filters.finalidade]);

  // Processar filtros da URL ou state (apenas na montagem inicial)
  useEffect(() => {
    const rawStateFilters = location.state?.filters || {};
    
    // Normalizar tipo para array se vier como string (do state)
    const stateFilters = { ...rawStateFilters };
    if (stateFilters.tipo && typeof stateFilters.tipo === 'string') {
      stateFilters.tipo = [stateFilters.tipo];
    }
    
    const urlFilters: ImoveisFilters = {};

    // Strings
    if (searchParams.get('finalidade')) {
      urlFilters.finalidade = searchParams.get('finalidade') as 'locacao' | 'venda';
    }
    if (searchParams.get('categoria')) {
      urlFilters.categoria = searchParams.get('categoria') as 'residencial' | 'comercial' | 'rural';
    }
    if (searchParams.get('padrao')) {
      urlFilters.padrao = searchParams.get('padrao') as ImoveisFilters['padrao'];
    }
    if (searchParams.get('search')) {
      urlFilters.search = searchParams.get('search')!;
    }
    
    // Arrays (comma-separated)
    if (searchParams.get('tipo')) {
      urlFilters.tipo = searchParams.get('tipo')!.split(',');
    }
    if (searchParams.get('diferenciais')) {
      urlFilters.diferenciais = searchParams.get('diferenciais')!.split(',');
    }
    if (searchParams.get('diferenciais_cond')) {
      urlFilters.diferenciais_condominio = searchParams.get('diferenciais_cond')!.split(',');
    }
    
    // Números
    const numericFields = ['quartos_min', 'suites_min', 'banheiros_min', 'vagas_min', 'area_min', 'area_max', 'valor_min', 'valor_max'] as const;
    numericFields.forEach(field => {
      const value = searchParams.get(field);
      if (value) (urlFilters as any)[field] = parseFloat(value);
    });
    
    // Ordenação e página da URL
    const urlSort = searchParams.get('sort') as SortOption;
    const urlPage = parseInt(searchParams.get('page') || '1');
    
    if (urlSort) setSortOrder(urlSort);
    if (urlPage > 1) setCurrentPage(urlPage);

    // Combinar filtros do state e URL (URL tem prioridade)
    const combinedFilters = { ...stateFilters, ...urlFilters };
    setFilters(combinedFilters);

    // Atualizar tab baseado na finalidade
    if (combinedFilters.finalidade === 'locacao') {
      setActiveTab('Alugar');
    } else if (combinedFilters.finalidade === 'venda') {
      setActiveTab('Vender');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Apenas na montagem inicial

  // Converter imóveis ordenados para PropertyCards
  const allPropertyCards = useMemo(() => {
    if (sortedImoveis && sortedImoveis.length > 0) {
      return imoveisToPropertyCards(sortedImoveis);
    }
    return [];
  }, [sortedImoveis]);

  // Filtrar por favoritos se ativo
  const propertyCards = useMemo(() => {
    if (!showOnlyFavorites) {
      return allPropertyCards;
    }
    
    // Filtrar apenas os que estão nos favoritos
    const favoriteIds = favorites.map(fav => String(fav.id));
    return allPropertyCards.filter(property => 
      favoriteIds.includes(String(property.id))
    );
  }, [allPropertyCards, showOnlyFavorites, favorites]);


  // Calcular total de páginas
  const totalPages = Math.ceil(propertyCards.length / pageSize);

  // Calcular número de filtros ativos (incluindo valores da busca)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    
    // Contar filtros básicos
    if (filters.tipo) count++;
    if (filters.finalidade) count++;
    if (filters.quartos_min) count++;
    if (filters.valor_max) count++;
    if (filters.search) count++;
    if (filters.bairros && filters.bairros.length > 0) count++;
    
    return count;
  }, [filters]);

  // Gerar localizações com geocoding real
  const { locations: propertyLocations, isGeocoding, progress } = useGeocodedProperties(
    propertyCards,
    imoveis
  );

  // Handler para mudança de busca
  const handleSearch = (searchFilters: any) => {
    const newFilters: ImoveisFilters = {};
    
    // Processar tipo de anúncio
    if (searchFilters.type === 'alugar') {
      newFilters.finalidade = 'locacao';
      setActiveTab('Alugar');
    } else if (searchFilters.type === 'vender') {
      newFilters.finalidade = 'venda';
      setActiveTab('Vender');
    } else {
      setActiveTab('Todos');
    }

    // Processar busca textual (cidade + bairro)
    const searchTerms = [];
    if (searchFilters.cidade?.trim()) {
      searchTerms.push(searchFilters.cidade.trim());
    }
    if (searchFilters.bairro?.trim()) {
      searchTerms.push(searchFilters.bairro.trim());
    }
    if (searchTerms.length > 0) {
      newFilters.search = searchTerms.join(' ');
    }

    // Processar valor máximo
    if (searchFilters.valorMax) {
      const valorNumerico = parseFloat(searchFilters.valorMax.replace(/[^\d]/g, ''));
      if (!isNaN(valorNumerico) && valorNumerico > 0) {
        newFilters.valor_max = valorNumerico;
      }
    }

    // Processar quartos mínimos
    if (searchFilters.quartos && searchFilters.quartos !== '0') {
      const quartosNum = parseInt(searchFilters.quartos);
      if (!isNaN(quartosNum) && quartosNum > 0) {
        newFilters.quartos_min = quartosNum;
      }
    }

    setFilters(newFilters);
    setCurrentPage(1);
    setShowOnlyFavorites(false);
    updateUrlWithFilters(newFilters, sortOrder, 1);
  };

  // Handler para toggle de favoritos
  const handleToggleFavorites = () => {
    setShowOnlyFavorites(prev => !prev);
    setCurrentPage(1); // Reset para primeira página
  };

  // Handler para contato
  const handleContactClick = () => {
    const whatsapp = siteConfig?.telefone_contato || '19999999999';
    const message = encodeURIComponent('Olá! Gostaria de mais informações.');
    window.open(`https://wa.me/55${whatsapp}?text=${message}`, '_blank');
  };

  // Handler para aplicar filtros do drawer
  const handleApplyFilters = (newFilters: ImoveisFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setShowOnlyFavorites(false);
    updateUrlWithFilters(newFilters, sortOrder, 1);
    
    // Atualizar tab baseado na finalidade
    if (newFilters.finalidade === 'locacao') {
      setActiveTab('Alugar');
    } else if (newFilters.finalidade === 'venda') {
      setActiveTab('Vender');
    } else {
      setActiveTab('Todos');
    }
  };

  // Handler para mudança de ordenação
  const handleSortChange = (newSort: SortOption) => {
    setSortOrder(newSort);
    updateUrlWithFilters(filters, newSort, currentPage);
  };

  // Handler para mudança de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlWithFilters(filters, sortOrder, page);
  };

  // Handler para hover de imóvel
  const handlePropertyHover = (propertyId: string | number | null) => {
    setHoveredPropertyId(propertyId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Wrapper com largura máxima para monitores ultrawide */}
      <div className="w-full max-w-[1920px] mx-auto">
        {/* Header */}
        <Header
          onNavigateToProperties={() => navigate('/search-grid')}
          onNavigateToAbout={() => navigate('/#about-section')}
          onAnnounceClick={() => navigate('/anuncie')}
        />

        {/* Search Bar Section */}
        <section className="w-full border-b border-gray-200">
          <div className="px-4 md:px-8 lg:px-20 py-6 lg:py-8">
            <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-end">
              {/* SearchBar */}
              <div className="flex-1 min-w-0 order-1">
                <SearchBar
                  variant="page"
                  showTabs
                  className="px-0"
                  showSearchButton={true}
                  onSearch={handleSearch}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 order-2">
                <FilterButton
                  onClick={() => setIsFilterDrawerOpen(true)}
                  filterCount={activeFilterCount}
                  label="Filtros"
                  variant="default"
                />
                <AlertButton
                  onClick={() => setIsAlertDrawerOpen(true)}
                  label="Criar um alerta"
                  variant="default"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="w-full py-10">
          {/* Container com layout flex para grid + mapa */}
          <div className="w-full flex gap-0">
            {/* Property Listings - Largura dinâmica (mobile sempre full width) */}
            <div
              className={`w-full ${showMap ? "lg:w-1/2" : ""} transition-all duration-300`}
            >
              <div className={`px-4 ${showMap ? "lg:px-8" : "lg:px-8"} flex flex-col items-start gap-6 lg:gap-10 w-full`}>
                {/* Header */}
                <div className="w-full">
                  {/* Badge de Favoritos Ativo */}
                  {showOnlyFavorites && (
                    <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600">
                      <Heart className="w-5 h-5 fill-red-500" />
                      <span className="font-medium">
                        Mostrando apenas seus favoritos ({propertyCards.length} {propertyCards.length === 1 ? 'imóvel' : 'imóveis'})
                      </span>
                      <button
                        onClick={handleToggleFavorites}
                        className="ml-auto text-sm underline hover:no-underline"
                      >
                        Mostrar todos
                      </button>
                    </div>
                  )}

                  {/* Badge de Filtro Ativo */}
                  {!showOnlyFavorites && (
                    <div className="mb-4 flex items-center gap-2">
                      {/* Exibir "Todos" apenas quando não há filtros específicos */}
                      {activeTab === 'Todos' && !filters.categoria && (!filters.tipo || filters.tipo.length === 0) && (
                        <span className="text-sm text-gray-600 font-normal">
                          Todos
                        </span>
                      )}
                      
                      {/* Exibir finalidade específica (Alugar/Comprar) quando selecionada */}
                      {activeTab !== 'Todos' && (
                        <span className="text-sm text-gray-600 font-normal">
                          {activeTab}
                        </span>
                      )}
                      
                      {/* Exibir categoria se selecionada */}
                      {filters.categoria && (
                        <span className="text-sm text-gray-600 font-normal">
                          • {filters.categoria.charAt(0).toUpperCase() + filters.categoria.slice(1)}
                        </span>
                      )}
                      
                      {/* Exibir tipos se selecionados (múltiplos) */}
                      {filters.tipo && filters.tipo.length > 0 && (
                        <span className="text-sm text-gray-600 font-normal">
                          • {filters.tipo.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')}
                        </span>
                      )}
                    </div>
                  )}

                  <SearchResultsHeader
                    totalResults={propertyCards.length}
                    location="Campinas, SP"
                    sortValue={sortOrder}
                    onSortChange={handleSortChange}
                    showMapButton={!showMap}
                    onMapClick={() => setShowMap(!showMap)}
                    mapLabel="Mostrar mapa"
                    showFavoritesButton={true}
                    favoritesCount={favoritesCount}
                    showOnlyFavorites={showOnlyFavorites}
                    onFavoritesClick={handleToggleFavorites}
                  />
                </div>

                {/* Loading State */}
                {loadingImoveis && (
                  <div className="w-full text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Carregando imóveis...</p>
                  </div>
                )}

                {/* Empty State - Favoritos */}
                {!loadingImoveis && showOnlyFavorites && propertyCards.length === 0 && (
                  <div className="w-full text-center py-12 px-4">
                    <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-bold text-neutral-black mb-2">
                      Nenhum favorito encontrado
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Você ainda não salvou nenhum imóvel como favorito. 
                      Comece clicando no ícone de coração nos imóveis que você gostar.
                    </p>
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={handleToggleFavorites}
                        className="px-6 py-3 rounded-full bg-neutral-black text-white hover:bg-neutral-black/90 transition-colors"
                      >
                        Ver Todos os Imóveis
                      </button>
                      <button
                        onClick={() => navigate('/')}
                        className="px-6 py-3 rounded-full border-2 border-neutral-black text-neutral-black hover:bg-gray-50 transition-colors"
                      >
                        Voltar ao Início
                      </button>
                    </div>
                  </div>
                )}

                {/* Empty State - Busca Geral */}
                {!loadingImoveis && !showOnlyFavorites && propertyCards.length === 0 && (
                  <div className="w-full text-center py-12">
                    <p className="text-muted-foreground mb-2">Nenhum imóvel encontrado</p>
                    <p className="text-sm text-muted-foreground">Tente ajustar os filtros de busca</p>
                  </div>
                )}

                {/* Property Cards Grid - Ajusta colunas quando mapa está visível (apenas desktop) */}
                {!loadingImoveis && propertyCards.length > 0 && (
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 ${showMap ? "md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4" : "md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"} w-full`}
                  >
                    {propertyCards
                      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                      .map((property) => (
                        <PropertyCard
                          key={property.id}
                          {...property}
                          variant="grid"
                          onClick={() => {
                            if (property.code) {
                              navigate(`/imovel/${property.code}`);
                            }
                          }}
                          onMouseEnter={() => handlePropertyHover(property.id)}
                          onMouseLeave={() => handlePropertyHover(null)}
                        />
                      ))}
                  </div>
                )}

                {/* Pagination */}
                {!loadingImoveis && propertyCards.length > pageSize && (
                  <nav
                    className="w-full flex justify-center mt-6"
                    aria-label="Paginação"
                  >
                    <ul className="flex items-center gap-2">
                      <li>
                        <button
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          className="h-9 px-3 rounded-full border border-gray-300 text-neutral-black hover:bg-neutral-black/5 disabled:opacity-40 transition-colors"
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </button>
                      </li>
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <li key={i}>
                          <button
                            onClick={() => handlePageChange(i + 1)}
                            className={`h-9 w-9 rounded-full border transition-colors ${
                              currentPage === i + 1
                                ? "bg-neutral-black text-white border-neutral-black"
                                : "border-gray-300 text-neutral-black hover:bg-neutral-black/5"
                            }`}
                            aria-current={
                              currentPage === i + 1 ? "page" : undefined
                            }
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li>
                        <button
                          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                          className="h-9 px-3 rounded-full border border-gray-300 text-neutral-black hover:bg-neutral-black/5 disabled:opacity-40 transition-colors"
                          disabled={currentPage === totalPages}
                        >
                          Próximo
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>

             {/* Mapa - Ocupa metade da tela quando visível (Apenas Desktop) */}
             {showMap && (
               <div className="hidden lg:block w-1/2 relative border-l border-gray-200">
                  {/* Indicador de Geocoding */}
                   {isGeocoding && (
                     <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white px-4 py-2 rounded-lg shadow-lg">
                       <p className="text-sm text-gray-600">
                         📍 {progress ? `Carregando ${progress}` : 'Carregando localizações...'}
                       </p>
                     </div>
                   )}
                 
                  {/* Botão "Esconder mapa" flutuante */}
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => setShowMap(false)}
                    className="absolute top-6 right-6 z-10 bg-white text-neutral-black border-none shadow-[0px_4px_16px_0px_rgba(0,0,0,0.12)] hover:bg-gray-100 hover:shadow-[0px_6px_20px_0px_rgba(0,0,0,0.16)] hover:scale-105"
                    aria-label="Esconder mapa"
                    rightIcon={<EyeClosed className="text-neutral-black" />}
                  >
                    Esconder mapa
                  </Button>

                  {/* Container sticky para o mapa */}
                  <div className="sticky top-0 h-screen">
                    <PropertyMap
                      properties={propertyLocations}
                      onPropertyClick={(id) => {
                        const property = propertyCards.find(p => String(p.id) === String(id));
                        if (property?.code) {
                          navigate(`/imovel/${property.code}`);
                        }
                      }}
                      center={[-22.8722, -47.0527]}
                      zoom={14}
                      className="h-screen"
                      highlightedPropertyId={hoveredPropertyId}
                    />
                  </div>
               </div>
             )}
          </div>
        </section>

        {/* Footer */}
        <Footer onContactClick={handleContactClick} />
      </div>

      {/* Floating Chat Button */}
      <FloatingChat
        position="bottom-right"
        size="md"
        onClick={handleContactClick}
      />

      {/* Alert Drawer */}
      <AlertDrawer
        isOpen={isAlertDrawerOpen}
        onClose={() => setIsAlertDrawerOpen(false)}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        currentFilters={filters}
        onApplyFilters={handleApplyFilters}
        availableCount={propertyCards.length}
      />
    </div>
  );
}
